import { api } from './client';
import type { Expense, ExpenseDebt, ReservationLink, ExpenseSummary, ExpenseCategory, PaymentMethod, BudgetsResponse, BudgetLimitInput, RedistributionPreview } from '../types';

/** One expense read out of an upload — a whole receipt, or one statement row. */
export interface ExtractedExpense {
  description?:   string;
  amount?:        number;
  currency?:      string;
  date?:          string;
  category?:      ExpenseCategory;
  paymentMethod?: PaymentMethod;
  status?:        'PAID' | 'PENDING';
}

export interface ExtractedExpenseData extends ExtractedExpense {
  /** Every expense found. A receipt yields one; a statement screenshot, one per transaction. */
  expenses:             ExtractedExpense[];
  aiCreditsLeft:        number;
  premiumAiCreditsLeft: number;
}

/** `reservation`: a link sets it, `null` clears it, omitted leaves it untouched. */
export type ExpenseWrite = Partial<Expense> & { reservation?: ReservationLink | null };

export const expensesApi = {
  list: (tripId: string, category?: ExpenseCategory) =>
    api.get<Expense[]>(`/trips/${tripId}/expenses`, { params: category ? { category } : {} }).then(r => r.data),
  create: (tripId: string, data: ExpenseWrite & { splits: { userId: string; amount: number }[] }) =>
    api.post<Expense>(`/trips/${tripId}/expenses`, data).then(r => r.data),
  update: (tripId: string, expenseId: string, data: ExpenseWrite) =>
    api.patch<Expense>(`/trips/${tripId}/expenses/${expenseId}`, data).then(r => r.data),
  delete: (tripId: string, expenseId: string) => api.delete(`/trips/${tripId}/expenses/${expenseId}`),
  summary: (tripId: string) =>
    api.get<ExpenseSummary>(`/trips/${tripId}/expenses/summary`).then(r => r.data),
  debts: (tripId: string) =>
    api.get<ExpenseDebt[]>(`/trips/${tripId}/expenses/debts`).then(r => r.data),
  settleDebt: (tripId: string, debtId: string, note?: string | null) =>
    api.post(`/trips/${tripId}/expenses/debts/settle`, { debtId, ...(note !== undefined ? { note } : {}) }),
  recalculateDebts: (tripId: string) =>
    api.post<ExpenseDebt[]>(`/trips/${tripId}/expenses/debts/recalculate`).then(r => r.data),
  redistributionPreview: (tripId: string) =>
    api.get<RedistributionPreview>(`/trips/${tripId}/expenses/redistribution`).then(r => r.data),
  applyRedistribution: (tripId: string) =>
    api.post<{ redistributed: number; debts: ExpenseDebt[] }>(`/trips/${tripId}/expenses/redistribution`).then(r => r.data),
  budgets: (tripId: string) =>
    api.get<BudgetsResponse>(`/trips/${tripId}/expenses/budgets`).then(r => r.data),
  setBudgets: (tripId: string, limits: BudgetLimitInput[]) =>
    api.patch<BudgetsResponse>(`/trips/${tripId}/expenses/budgets`, { limits }).then(r => r.data),
  extract: (tripId: string, files: File[]) => {
    const form = new FormData();
    files.forEach(file => form.append('files', file));
    return api.post<ExtractedExpenseData>(
      `/trips/${tripId}/expenses/extract`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    ).then(r => r.data);
  },
};
